import { Injectable } from '@angular/core';
import { ApiBaseService } from '@core/services/api-base.service';
import { Mapper } from '@core/services/mapper/base-mapper.mapper';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { HttpParams } from '@angular/common/http';
import { GeneralResponse } from '@shared/enums/general-response.enum';
import { ExportedFile } from '@shared-features/models/exported-file';
import { CountsType } from '@shared-features/enums/counts-type.enum';
import { CustomerFleetType } from '@shared-features/enums/customer-fleet-type.enum';
import { filterNullEntity } from '@shared/utils/filter-null-entity.util';
import { ApiConstant } from '@shared/constants/api.constant';
import { DriverShipmentCounts } from '@features/fleets/models/driver-shipment-counts.model';
import { DriverBlock } from '@shared-features/models/block-status';
import { Driver } from '../models/driver';
import { Elm } from '../models/elm';
import { ShipmentStatusCount } from '@features/shipments/models/shipment-status-count';
import { DriverDetails } from '../models/driver-details';
import { VendorSource } from '@shared-features/enums/vendor-source.enum';

@Injectable({
  providedIn: 'root',
})
export class DriversApiService {
  constructor(private baseAPI: ApiBaseService, private mapper: Mapper) {}

  // fleets
  getAllDrivers(
    fleetId: number,
    searchTerm?: string,
    offset?: number,
    limit?: number,
    appDownloadedFilter?: string,
    vendorSource?: VendorSource[]
  ): Observable<DriverDetails> {
    let params = new HttpParams();
    if (fleetId) params = params.append('fleetId', fleetId.toString());
    if (searchTerm) params = params.append('searchTerm', searchTerm);
    if (offset !== null) params = params.append('offset', offset.toString());
    if (limit) params = params.append('limit', limit.toString());
    if (appDownloadedFilter) params = params.append('appDownloadedFilter', appDownloadedFilter);
    if (vendorSource?.length) vendorSource.forEach((v) => params = params.append('vendorSource[]', v));

    return this.baseAPI.get(ApiConstant.GET_ALL_DRIVERS, { params: params }).pipe(
      map((res) => {
        return this.mapper.fromJson(DriverDetails, res.data);
      })
    );
  }

  // fleets
  addDriver(driver: Driver, isLead: boolean = false): Observable<Driver> {
    const body = {
      firstName: driver.user?.firstName,
      lastName: driver.user?.lastName,
      fleetId: driver.fleet?.id,
      licenseAttachment: driver.licenseAttachment?.id,
      iqamaAttachment: driver.iqamaAttachment?.id,
      mobileNumber: driver.mobileNumber,
      nationality: driver.nationality,
      profilePictureId: driver.logo?.id,
      truckId: driver.truck?.id,
      birthDate: driver.birthDate,
      isLead: isLead,
      permits: driver.permits || [],
    };

    if (driver.licenseExpiry) {
      body['licenseExpiry'] = driver.licenseExpiry;
    }

    if (driver.iqamaId) {
      body['iqamaId'] = driver.iqamaId;
    }

    if (driver.nonResidentId) {
      body['nonResidentId'] = driver.nonResidentId;
    }

    if (driver.owner) {
      body['owner'] = driver.owner;
    }

    if (driver.licenseType) {
      body['licenseType'] = driver.licenseType;
    }

    if (driver.iqamaIdExpiry) {
      body['iqamaIdExpiry'] = driver.iqamaIdExpiry;
    }

    if (driver.issueNumber) {
      body['issueNumber'] = driver.issueNumber;
    }

    return this.baseAPI.post(ApiConstant.ADD_DRIVER, body).pipe(
      map((res) => {
        return this.mapper.fromJson(Driver, res.data);
      })
    );
  }

  // fleets
  updateDriver(
 body:Partial <Driver>
  ): Observable<Driver> {
    return this.baseAPI.put(ApiConstant.UPDATE_DRIVER, body).pipe(
      map((res) => {
        return this.mapper.fromJson(Driver, res.data);
      })
    );
  }

  // fleets
  verifyByElm(payload: {
    iqamaId: number;
    birthDate: Date;
    driverId: number;
    elmObj: any;
  }): Observable<Elm> {
    return this.baseAPI.put(ApiConstant.VERIFY_BY_3LEM, payload).pipe(
      map((res) => {
        return this.mapper.fromJson(Elm, res.data);
      })
    );
  }

  // fleets
  sendPassword(driverId): Observable<GeneralResponse> {
    const body = { driverId };
    return this.baseAPI.post(ApiConstant.SEND_PASSWORD, body).pipe(
      map((res) => {
        return res;
      })
    );
  }

  // fleets
  getDriverShipments(driverId: number): Observable<ShipmentStatusCount> {
    return this.baseAPI.get(ApiConstant.GET_DRIVER_SHIPMENTS + driverId).pipe(
      map((res) => {
        return this.mapper.fromJson(ShipmentStatusCount, res.data);
      })
    );
  }

  // fleets
  moveToFleet(body: { fleetId: number; driverId: number; truckId: number }) {
    return this.baseAPI.put(ApiConstant.DRIVER_MOVE_TO_FLEET, filterNullEntity<typeof body>(body));
  }

  // fleets
  filterDriverByFleetType(body: {
    fleetType: CustomerFleetType;
    fleetId?: number;
    driverIds: number[];
  }): Observable<Driver[]> {
    return this.baseAPI.post(
      ApiConstant.FILTER_DRIVER_BY_FLEET_TYPE,
      filterNullEntity<typeof body>(body)
    );
  }

  exportDrivers(
    fleetId: number,
    offset?: number,
    limit?: number,
    searchTerm?: string,
    appDownloadedFilter?: string
  ): Observable<ExportedFile> {
    let params = filterNullEntity({
      fleetId,
      searchTerm,
      offset,
      limit,
      appDownloadedFilter,
    });
    return this.baseAPI.get(ApiConstant.API_GET_EXPORT_DRIVERS, { params: params }).pipe(
      map((res) => {
        return this.mapper.fromJson(ExportedFile, res.data);
      })
    );
  }

  releaseDriver(id: number) {
    return this.baseAPI.put(ApiConstant.RELEASE_DRIVER + `/${id}`, { id });
  }

  getShipmentCounts(payload: {
    customerId: number;
    id: number;
    countsType: CountsType;
  }): Observable<DriverShipmentCounts> {
    return this.baseAPI
      .get(ApiConstant.GET_SHIPMENT_INFO_COUNTS, { params: filterNullEntity(payload) })
      .pipe(
        map((res) => {
          return this.mapper.fromJson(DriverShipmentCounts, res.data);
        })
      );
  }

  updateDriverBlacklistPerCustomers(payload: {
    driverID: number;
    customersID: DriverBlock;
    reason: string;
  }): Observable<String> {
    return this.baseAPI.post(ApiConstant.UPDATE_DRIVER_BLACKLIST, payload).pipe(
      map((res) => {
        return this.mapper.fromJson(String, res.data);
      })
    );
  }
}
